import { Guid } from '@Shared.Angular/@types/guid';
import angular from 'angular';
import { IFormattedCategory } from './start.flows.formatter';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import ICategoryTree from '@Shared.Angular/@types/core/contracts/interfaces/categoryTree';
import { naturalSortComparator } from '@Shared.Angular/flowingly.helper/naturalSort';

/**
 * @deprecated Don't use this interface, it's a mess. Should not have name & text, or items & flowmodels
 */
export interface ICategorySource<T> {
  name?: string | null;
  id: Guid | string;
  flowModels?: T[];
  subCategories?: ICategorySource<T>[];
  text?: string;
  items?: ICategorySource<T>[];
}

export interface ITreeCategory<T> {
  id: string;
  /** This is actually whatever T is in the Category, ToDo rename 'items' */
  flowModels: T[];
  /** This is actually the child Categories, ToDo rename 'subCategories' */
  items: ITreeCategory<T>[];
  text: string;
  count?: number;
}

function sortTreeCategoriesNaturally<T>(categories: ITreeCategory<T>[]) {
  categories.sort((leftCategory, rightCategory) =>
    naturalSortComparator(leftCategory.text, rightCategory.text)
  );

  for (const category of categories) {
    if (category.items?.length > 0) {
      sortTreeCategoriesNaturally(category.items);
    }
  }

  return categories;
}

export class RunnerCategoryNavigationService {
  getFormattedReportTreeData = <T>(data: ICategorySource<T>[]) => {
    const formattedData: ITreeCategory<T>[] = [];
    data.forEach((category) => {
      const formattedCategory = {
        items: [],
        id: category.id,
        text: category.name,
        flowModels: category.flowModels
      };

      if (category.subCategories?.length > 0) {
        formattedCategory.items = this.getFormattedReportTreeData(
          category.subCategories
        );
      }

      formattedData.push(formattedCategory);
    });

    return formattedData;
  };

  convertToTreeViewCompatibleCategories = <T>(
    categories: IFormattedCategory<T>[]
  ): ITreeCategory<T>[] => {
    return categories.map((category) => {
      return {
        id: category.id,
        text: category.name,
        flowModels: category.flowModels || [],
        items: category.subCategories
          ? this.convertToTreeViewCompatibleCategories(category.subCategories)
          : []
      };
    });
  };

  getFlowModelsFromNestedCategories<T>(categories: ITreeCategory<T>[]) {
    const allFlowModels: T[] = [];

    function extractFlowModels(categories: ITreeCategory<T>[]) {
      categories.forEach((category) => {
        if (category.flowModels?.length > 0) {
          allFlowModels.push(...category.flowModels);
        }
        if (category.subCategories?.length > 0) {
          extractFlowModels(category.subCategories);
        }
        if (category.items?.length > 0) {
          extractFlowModels(category.items);
        }
      });
    }

    extractFlowModels(categories);
    return allFlowModels;
  }

  findNodeById<T>(nodes: ITreeCategory<T>[], id: string): ITreeCategory<T> {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      } else if (node.items?.length > 0) {
        const foundNode = this.findNodeById<T>(node.items, id);
        if (foundNode) {
          return foundNode;
        }
      } else if (node.subCategories?.length > 0) {
        const foundNode = this.findNodeById<T>(node.subCategories, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }

  removeItem = (
    categories: ITreeCategory<{ id: Guid } | { entityId: Guid }>[],
    idToRemove: Guid
  ): boolean => {
    let removed = false;
    const removeFromCategory = (
      category: ITreeCategory<{ id: Guid } | { entityId: Guid }>
    ) => {
      const itemIndex = category.flowModels.findIndex((item) => {
        return (
          ('id' in item && item.id) === idToRemove ||
          ('entityId' in item && item.entityId) === idToRemove
        );
      });
      if (itemIndex !== -1) {
        category.flowModels.splice(itemIndex, 1);
        removed = true;
      }
      category.items.forEach((subCategory) => removeFromCategory(subCategory));
    };
    for (const category of categories) {
      // Call for all categories as may be in multiple, e.g. All and X
      removeFromCategory(category);
    }
    return removed;
  };

  addItem = (
    categories: ITreeCategory<any>[],
    allCategoriesId: string,
    item: ICategoryTree
  ): void => {
    const allCategory = categories.find((c) => c.id === allCategoriesId);
    if (allCategory) {
      allCategory.flowModels.push(item);
    }
    let targetCategory = item.categoryTree;
    let storedCategory = categories.find(
      (c) => c.id === targetCategory.categoryId
    );
    if (!storedCategory) {
      storedCategory = {
        id: targetCategory.categoryId,
        text: targetCategory.categoryName,
        flowModels: [],
        items: []
      };
      categories.push(storedCategory);
    }
    targetCategory = targetCategory.category;
    let parentStoredCategory: ITreeCategory<IPublishedFlowModel> | undefined;
    let destinationStoredCategory = storedCategory;
    while (targetCategory) {
      parentStoredCategory = storedCategory;
      storedCategory = storedCategory.items.find(
        (c) => c.id === targetCategory.categoryId
      );
      if (!storedCategory) {
        storedCategory = {
          id: targetCategory.categoryId,
          text: targetCategory.categoryName,
          flowModels: [],
          items: []
        };
        parentStoredCategory.items.push(storedCategory);
      }
      destinationStoredCategory = storedCategory;
      targetCategory = targetCategory.category;
    }
    destinationStoredCategory.flowModels.push(item);

    const allCategoryIndex = categories.findIndex(
      (category) => category.id === allCategoriesId
    );

    if (allCategoryIndex >= 0) {
      const [allCategory] = categories.splice(allCategoryIndex, 1);
      sortTreeCategoriesNaturally(categories);
      categories.unshift(allCategory);
      return;
    }

    sortTreeCategoriesNaturally(categories);
  };
}

angular
  .module('flowingly.runner.services')
  .factory(
    'runnerCategoryNavigationService',
    () => new RunnerCategoryNavigationService()
  );

export type RunnerCategoryNavigationServiceType = InstanceType<
  typeof RunnerCategoryNavigationService
>;
